// #609: Reiter „Zuletzt hinzugefügt“ – neue Dateien aller Objekte in einer großen Liste, nach Tag gruppiert.
// Filter Zeitraum / Objekt / Herkunft / Suche; Klick öffnet die Datei im Aktenschrank. Mehrfachauswahl mit
// Portal-Freigabe wie im Aktenschrank (#607).
import { useEffect, useMemo, useState } from 'react';
import { ArrowClockwise, CircleNotch, ClockCounterClockwise, DownloadSimple, Globe, LockSimple, MagnifyingGlass, PencilSimple, X } from '@phosphor-icons/react';
import { BestaetigenDialog, ImmoButton, LeerZustand, ObjektPicker, SchwebenderToast, useToastPill, type ObjektPickerOption } from '../../../../components/immobilien/ui';
import { Segment } from '../../../../components/ui/Segment';
import { AuswahlKaestchen } from '../../../../components/ui/AuswahlKaestchen';
import { FreigabeDialog } from '../../../../components/dokumente/FreigabeDialog';
import { PapierkorbKnopf } from '../../../../components/dokumente/PapierkorbKnopf';
import { useMobilModus } from '../../../../lib/immobilien/mobil';
import { ladeObjekte, type AktObjekt } from '../../../../lib/dokumente/useAktenschrank';
import { ladeDateiBlob } from '../../../../lib/dokumente/aktenschrankApi';
import {
  FREIGABEN_GEAENDERT, entziehe, ladeFreigaben, meldeFreigabenGeaendert, nachDokument, type Freigabe, type FreigabeDok,
} from '../../../../lib/dokumente/freigaben';
import { HERKUENFTE, ZEITRAEUME, herkunftText, ladeZuletzt, ordnerText, type Herkunft, type Zeitraum, type ZuletztEintrag } from '../../../../lib/dokumente/zuletzt';
import { FreigabeAbzeichen } from '../aktenschrank/OrdnerInhalt';
import { dateiIcon } from '../kopf/DokumenteWidgets';

const ALLE = 'alle';

function tagKopf(iso: string): string {
  const d = new Date(iso);
  const heute = new Date();
  const tag = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((tag(heute) - tag(d)) / 86_400_000);
  if (diff === 0) return 'Heute';
  if (diff === 1) return 'Gestern';
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: d.getFullYear() === heute.getFullYear() ? undefined : 'numeric' });
}
const uhrzeit = (iso: string) => new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

export function ZuletztTab({ onOeffnen }: { onOeffnen: (e: ZuletztEintrag) => void }) {
  const { mobil } = useMobilModus();
  const [toast, zeigeToast] = useToastPill(3200);
  const [objekte, setObjekte] = useState<AktObjekt[]>([]);
  const [zeitraum, setZeitraum] = useState<Zeitraum>('7');
  const [objektId, setObjektId] = useState<string | null>(null);
  const [herkunft, setHerkunft] = useState<Herkunft>('alle');
  const [suche, setSuche] = useState('');
  const [sucheVerzoegert, setSucheVerzoegert] = useState('');
  const [eintraege, setEintraege] = useState<ZuletztEintrag[]>([]);
  const [seite, setSeite] = useState(0);
  const [mehr, setMehr] = useState(false);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [runde, setRunde] = useState(0);
  const [freigaben, setFreigaben] = useState<ReadonlyMap<string, Freigabe[]>>(new Map());
  const [freigabeRunde, setFreigabeRunde] = useState(0);
  const [markiert, setMarkiert] = useState<ReadonlySet<string>>(new Set());
  const [dialog, setDialog] = useState<null | 'neu' | 'aendern'>(null);
  const [entziehenFrage, setEntziehenFrage] = useState(false);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => { void ladeObjekte().then(setObjekte, () => setObjekte([])); }, []);
  useEffect(() => { const t = setTimeout(() => setSucheVerzoegert(suche), 300); return () => clearTimeout(t); }, [suche]);

  // Filter geändert → von vorn
  useEffect(() => { setSeite(0); setMarkiert(new Set()); }, [zeitraum, objektId, herkunft, sucheVerzoegert, runde]);
  useEffect(() => {
    let aktiv = true;
    setLaedt(true); setFehler(null);
    ladeZuletzt({ zeitraum, objektId, herkunft, suche: sucheVerzoegert }, seite)
      .then(r => { if (!aktiv) return; setEintraege(alt => (seite === 0 ? r.eintraege : [...alt, ...r.eintraege])); setMehr(r.mehr); })
      .catch((e: unknown) => { if (aktiv) setFehler(e instanceof Error ? e.message : 'Liste konnte nicht geladen werden'); })
      .finally(() => { if (aktiv) setLaedt(false); });
    return () => { aktiv = false; };
  }, [zeitraum, objektId, herkunft, sucheVerzoegert, seite, runde]);

  const ids = useMemo(() => eintraege.map(e => e.id), [eintraege]);
  useEffect(() => {
    if (!ids.length) { setFreigaben(new Map()); return; }
    let aktiv = true;
    ladeFreigaben(ids).then(f => { if (aktiv) setFreigaben(nachDokument(f)); }, () => { if (aktiv) setFreigaben(new Map()); });
    return () => { aktiv = false; };
  }, [ids, freigabeRunde]);
  useEffect(() => {
    const neu = () => setFreigabeRunde(r => r + 1);
    window.addEventListener(FREIGABEN_GEAENDERT, neu);
    return () => window.removeEventListener(FREIGABEN_GEAENDERT, neu);
  }, []);

  const optionen: ObjektPickerOption[] = useMemo(() => [
    { id: ALLE, label: 'Alle Objekte', nummer: '', sub: 'ohne Einschränkung', suchtext: 'alle' },
    ...objekte.filter(o => !o.pseudo).map(o => ({ id: o.id, label: o.name, nummer: o.nummer, sub: [o.verwaltungsart, o.ort].filter(Boolean).join(' · '), suchtext: `${o.nummer} ${o.ort}` })),
  ], [objekte]);

  const gruppen = useMemo(() => {
    const g: { kopf: string; eintraege: ZuletztEintrag[] }[] = [];
    for (const e of eintraege) {
      const k = tagKopf(e.created_at);
      if (g[g.length - 1]?.kopf === k) g[g.length - 1]!.eintraege.push(e); else g.push({ kopf: k, eintraege: [e] });
    }
    return g;
  }, [eintraege]);

  const markierte = eintraege.filter(e => markiert.has(e.id));
  const doks: FreigabeDok[] = markierte.map(e => ({
    id: e.id, titel: e.titel || e.datei_name || 'Dokument', objekt_id: e.objekt_id, mieter_id: e.mieter_id,
    eigentuemer_id: e.eigentuemer_id, kategorie: e.kategorie, dokument_typ: e.dokument_typ,
  }));
  const markierteFrei = markierte.filter(e => (freigaben.get(e.id)?.length ?? 0) > 0).length;
  const alleMarkiert = eintraege.length > 0 && eintraege.every(e => markiert.has(e.id));
  const umschalten = (id: string) => setMarkiert(alt => { const n = new Set(alt); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const herunterladen = async () => {
    for (const e of markierte) {
      try {
        const blob = await ladeDateiBlob(e.gdrive_file_id, null);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = e.datei_name || e.titel || 'Datei'; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
      } catch (err) { zeigeToast(`${e.datei_name ?? 'Datei'}: ${err instanceof Error ? err.message : 'Download fehlgeschlagen'}`, true); }
    }
  };
  const allesEntziehen = async () => {
    setLaeuft(true);
    const f = await entziehe(doks.map(d => d.id), null);
    setLaeuft(false); setEntziehenFrage(false);
    if (f) { zeigeToast(`Nicht entzogen: ${f}`, true); return; }
    zeigeToast('Freigaben entzogen');
    meldeFreigabenGeaendert();
  };

  return (
    <div className="flex flex-col min-w-0" style={{ gap: 14 }}>
      <div className="immo-glas flex flex-wrap items-center" style={{ borderRadius: 22, padding: 10, gap: 10 }}>
        <Segment ariaLabel="Zeitraum" hoehe={mobil ? 44 : 34} wert={zeitraum} onChange={setZeitraum} eintraege={ZEITRAEUME} />
        <ObjektPicker optionen={optionen} wert={objektId ?? ALLE} onWahl={id => setObjektId(id === ALLE ? null : id)} leerLabel="Alle Objekte" />
        <Segment ariaLabel="Herkunft" hoehe={mobil ? 44 : 34} wert={herkunft} onChange={setHerkunft} eintraege={HERKUENFTE.map(h => ({ key: h.key, label: h.label, tip: h.tip }))} />
        <label className="flex items-center" style={{ flex: '1 1 200px', minWidth: 0, height: mobil ? 44 : 38, borderRadius: 14, gap: 8, padding: '0 12px', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
          <MagnifyingGlass size={15} color="var(--ink3)" aria-hidden />
          <input value={suche} onChange={e => setSuche(e.target.value)} placeholder="Name oder Kategorie" aria-label="Zuletzt hinzugefügt durchsuchen"
            className="flex-1 min-w-0" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: 'var(--ink)' }} />
          {suche && <button type="button" aria-label="Suche leeren" onClick={() => setSuche('')} style={{ border: 'none', background: 'none', color: 'var(--ink3)', cursor: 'pointer' }}><X size={12} weight="bold" /></button>}
        </label>
        <ImmoButton icon={ArrowClockwise} aria-label="Neu laden" data-tip="Neu laden" onClick={() => setRunde(r => r + 1)} />
      </div>

      <section className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 26, padding: 14, gap: 8 }} aria-label="Zuletzt hinzugefügte Dateien">
        {eintraege.length > 0 && (
          <div className="flex items-center flex-wrap" style={{ gap: 8, padding: '6px 8px', borderRadius: 16, background: markiert.size ? 'var(--acc-soft)' : 'var(--fill)' }}>
            <AuswahlKaestchen an={alleMarkiert} label="Alle geladenen Dateien auswählen" touch={mobil}
              onUmschalten={() => setMarkiert(alleMarkiert ? new Set() : new Set(eintraege.map(e => e.id)))} />
            <span style={{ fontSize: 13, fontWeight: 800 }}>{markiert.size ? `${markiert.size} ausgewählt` : `${eintraege.length}${mehr ? '+' : ''} Dateien`}</span>
            {markiert.size > 0 && (
              <span className="flex flex-wrap items-center" style={{ gap: 6, marginLeft: 'auto' }}>
                <ImmoButton art="akzent" icon={Globe} onClick={() => setDialog('neu')}>Freigeben</ImmoButton>
                <ImmoButton icon={PencilSimple} onClick={() => setDialog('aendern')}>Freigabe ändern</ImmoButton>
                <ImmoButton icon={LockSimple} onClick={() => setEntziehenFrage(true)} disabled={!markierteFrei}>Entziehen</ImmoButton>
                <ImmoButton icon={DownloadSimple} onClick={() => { void herunterladen(); }}>Herunterladen</ImmoButton>
                <PapierkorbKnopf eintraege={markierte.map(e => ({ driveId: e.gdrive_file_id, name: e.datei_name || e.titel || 'Datei', dokumentId: e.id }))}
                  zeige={zeigeToast} onFertig={() => { setMarkiert(new Set()); setRunde(r => r + 1); }} />
                <ImmoButton icon={X} aria-label="Auswahl aufheben" onClick={() => setMarkiert(new Set())} />
              </span>
            )}
          </div>
        )}

        {fehler && <span role="alert" style={{ fontSize: 13, color: 'var(--bad)', padding: 8 }}>{fehler}</span>}
        {!laedt && !fehler && eintraege.length === 0 && (
          <LeerZustand icon={ClockCounterClockwise} titel="Keine neuen Dateien" text="Im gewählten Zeitraum wurde mit diesen Filtern nichts hinzugefügt." />
        )}

        {gruppen.map(g => (
          <div key={g.kopf} className="flex flex-col" style={{ gap: 2 }}>
            <span className="immo-zahl" style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ink3)', padding: '10px 10px 4px' }}>{g.kopf}</span>
            {g.eintraege.map(e => {
              const IconKomp = dateiIcon(e.datei_name);
              const name = e.datei_name || e.titel || 'Datei';
              const an = markiert.has(e.id);
              const objekt = [e.objekt?.objektnummer, e.objekt?.bezeichnung].filter(Boolean).join(' ');
              return (
                <div key={e.id} role="button" tabIndex={0} onClick={() => onOeffnen(e)}
                  onKeyDown={ev => { if ((ev.key === 'Enter' || ev.key === ' ') && ev.target === ev.currentTarget) { ev.preventDefault(); onOeffnen(e); } }}
                  className="immo-hover-flaeche flex items-center w-full cursor-pointer min-w-0"
                  style={{ gap: 12, minHeight: 60, padding: '8px 10px', borderRadius: 16, flexWrap: mobil ? 'wrap' : undefined, background: an ? 'var(--fill)' : 'transparent' }}>
                  <AuswahlKaestchen an={an} label={`${name} auswählen`} touch={mobil} onUmschalten={() => umschalten(e.id)} />
                  <span className="flex items-center justify-center shrink-0" style={{ width: 40, height: 40, borderRadius: 13, background: 'var(--acc-soft)' }}>
                    <IconKomp size={19} weight="fill" color="var(--acc)" />
                  </span>
                  <span className="flex flex-col flex-1 min-w-0" style={{ gap: 1 }}>
                    <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }} title={name}>{name}</span>
                    <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>
                      {[objekt, ordnerText(e.ablage_pfad), e.kategorie].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <FreigabeAbzeichen freigaben={freigaben.get(e.id) ?? []} />
                  <span className="flex flex-col items-end shrink-0" style={{ gap: 1, minWidth: 90 }}>
                    <span className="immo-zahl" style={{ fontSize: 13, fontWeight: 700 }}>{uhrzeit(e.created_at)}</span>
                    <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{herkunftText(e)}</span>
                  </span>
                </div>
              );
            })}
          </div>
        ))}

        {laedt && <div className="flex justify-center" style={{ padding: 20 }}><CircleNotch size={22} className="animate-spin" color="var(--ink3)" aria-label="Lädt" /></div>}
        {!laedt && mehr && (
          <div className="flex justify-center" style={{ padding: 8 }}>
            <ImmoButton onClick={() => setSeite(s => s + 1)}>Weitere laden</ImmoButton>
          </div>
        )}
      </section>

      {dialog && doks.length > 0 && (
        <FreigabeDialog dokumente={doks} aendern={dialog === 'aendern'} onSchliessen={() => setDialog(null)}
          onFertig={m => { setDialog(null); zeigeToast(m); setMarkiert(new Set()); meldeFreigabenGeaendert(); }} />
      )}
      {entziehenFrage && (
        <BestaetigenDialog titel="Freigaben entziehen?" bestaetigenLabel="Entziehen" icon={LockSimple} laeuft={laeuft}
          text={`${markierteFrei === 1 ? '1 Dokument ist' : `${markierteFrei} Dokumente sind`} danach in keinem Portal mehr sichtbar.`}
          onBestaetigen={() => { void allesEntziehen(); }} onAbbrechen={() => setEntziehenFrage(false)} />
      )}
      <SchwebenderToast toast={toast} mobil={mobil} />
    </div>
  );
}
